import {
  StoreCategoryValue,
  StoreTagValue,
  ParentStoreCategoryValue,
  STORE_CATEGORIES,
  STORE_CATEGORIES_PARENTS,
  STORE_TAGS,
  StoreCategory,
} from '@bounty/constants'
import {
  Button,
  H4,
  Sheet,
  Text,
  XStack,
  YStack,
  CaretDownIcon,
  PageLoading,
  CheckIcon,
  useAnalytics,
  LinkButton,
} from '@bounty/creators-design-system'
import { useState } from 'react'
import { createParam } from 'solito'
import { useLink } from 'solito/link'
import { buildShopCategoryGroupings, buildShopTags } from './buildShopFilters'
import { SORT_ORDERS } from './const'
import { useShopListContext } from './ShopListContext'
import { useSWRBounty } from '../../../hooks/swr'

export type ShopFiltersProps = unknown

/**
 * Returns a prioritized active filter
 */
const getActiveFilter = ({
  activeParentCategory,
  activeCategory,
  activeTag,
}: {
  activeParentCategory?: string
  activeCategory?: string
  activeTag?: string
}) => {
  if (activeCategory) {
    const maybeCategory = STORE_CATEGORIES.find(
      (category) =>
        category.slug === activeCategory &&
        activeParentCategory ===
          STORE_CATEGORIES_PARENTS.find(
            (parent) => parent.value === category.parent,
          )?.slug,
    )

    if (!maybeCategory) {
      throw new Error(
        `Cannot find category for: ${activeCategory}. Parent ${activeParentCategory}`,
      )
    }

    return maybeCategory
  }

  if (activeParentCategory) {
    const maybeParentCategory = STORE_CATEGORIES_PARENTS.find(
      (category) => category.slug === activeParentCategory,
    )

    if (!maybeParentCategory) {
      throw new Error(
        `Cannot find parent category for: ${activeParentCategory}`,
      )
    }

    return maybeParentCategory
  }

  if (activeTag) {
    const maybeTag = STORE_TAGS.find((category) => category.slug === activeTag)

    if (!maybeTag) {
      throw new Error(`Cannot find tag for: ${activeTag}`)
    }

    return maybeTag
  }
}

const ShopFilterChildItem = ({
  item,
  rootCategorySlug,
  tagMode = false,
  onPress,
}: {
  rootCategorySlug?: string
  tagMode?: boolean
  item: { value: string; label: string; slug: string }
  onPress: () => void
}) => {
  return (
    <LinkButton
      href={`/shop/${tagMode ? 'tags' : 'categories'}${
        rootCategorySlug ? `/${rootCategorySlug}` : ''
      }/${item.slug}`}
      variant="ghost"
      justifyContent="flex-start"
      colorScheme="whiteAlpha"
      buttonTextProps={{
        color: '$neutral.900',
        fontWeight: '$regular',
      }}
      data-testid={item.value}
      event={{
        eventName: 'Storefront Category Filter Item Clicked',
        filter: item.value,
        isChildCategory: true,
      }}
      borderBottomWidth={1}
      borderColor={'$neutral.300'}
      px="$0"
      onPress={onPress}
    >
      {item.label}
    </LinkButton>
  )
}

const ShopFilterItem = ({
  item,
  tagMode = false,
  onPress,
  onChildItemPress,
}: {
  tagMode?: boolean
  item: {
    label: string
    value: string
    slug?: string
    children: { value: string; label: string; slug: string }[] | null
  }
  onPress: () => void
  onChildItemPress: () => void
}) => {
  const { track } = useAnalytics()
  const linkProps = useLink({
    href: `/shop/${tagMode ? 'tags' : 'categories'}/${item.slug}`,
  })

  return (
    <YStack space="$1">
      <H4
        size="$lg"
        data-testid={item.value}
        onPress={() => {
          if (tagMode === false) {
            track('Storefront Category Parent Filter Item Clicked', {
              filter: item.value,
              isChildCategory: false,
            })
          }
          onPress()
        }}
        {...(tagMode === false && linkProps)}
      >
        {item.label}
      </H4>
      {item.children ? (
        <YStack>
          {item.children.map((child) => {
            return (
              <ShopFilterChildItem
                tagMode={tagMode}
                key={child.value}
                rootCategorySlug={item.slug}
                item={child}
                onPress={onChildItemPress}
              />
            )
          })}
        </YStack>
      ) : null}
    </YStack>
  )
}

const SortOrders = ({ onItemPress }: { onItemPress: () => void }) => {
  const [{ sortOrder }, setShopList] = useShopListContext()

  return (
    <YStack space="$1">
      {SORT_ORDERS.map((order) => {
        return (
          <XStack key={order.value}>
            <Button
              variant="ghost"
              flex={1}
              event={{
                eventName: 'Sort order pressed',
                sortOrder: order.value,
              }}
              data-testid={order.value}
              onPress={() => {
                setShopList((x) => ({ ...x, sortOrder: order.value }))
                onItemPress()
              }}
              justifyContent="space-between"
              colorScheme="neutral"
              borderTopWidth={1}
              borderColor={'$neutral.300'}
              buttonTextProps={{
                fontWeight: '$regular',
              }}
              iconRight={sortOrder === order.value ? CheckIcon : null}
            >
              {order.name}
            </Button>
          </XStack>
        )
      })}
    </YStack>
  )
}

const { useParam } = createParam()

export const ShopFilters = (_props: ShopFiltersProps) => {
  const { data } = useSWRBounty('getStorefrontFilters')
  const [activeCategory] = useParam<'category', StoreCategoryValue>('category')
  const [activeParentCategory] = useParam<
    'parentCategory',
    ParentStoreCategoryValue
  >('parentCategory')
  const [activeTag] = useParam<'tag', StoreTagValue>('tag')
  const [{ sortOrder }] = useShopListContext()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)
  const { track } = useAnalytics()
  const shopAllLinkProps = useLink({
    href: `/shop`,
  })

  if (!data) {
    return <PageLoading />
  }

  const activeCategoryGroupings = buildShopCategoryGroupings({
    categories: data.categories,
  })

  const activeTags = buildShopTags({
    tags: data.tags,
  })
  const activeFilter = getActiveFilter({
    activeCategory,
    activeTag,
    activeParentCategory,
  })
  const activeSortOrder =
    SORT_ORDERS.find((x) => x.value === sortOrder) ?? SORT_ORDERS[0]

  return (
    <>
      <XStack justifyContent="space-between" alignItems="center">
        <XStack flex={1}>
          <Button
            variant="ghost"
            justifyContent="flex-start"
            colorScheme="neutral"
            event="Storefront Show Filters Clicked"
            onPress={() => setFiltersOpen(true)}
            data-testid="shopFiltersCategories"
          >
            <XStack space="$1" display="flex" alignItems="center">
              <Text numberOfLines={1} fontWeight="$semibold" size="$xs">
                {(activeFilter as unknown as StoreCategory)?.labelLong ??
                  activeFilter?.label ??
                  'Categories'}
              </Text>
              <CaretDownIcon color="$neutral.900" weight="bold" size={16} />
            </XStack>
          </Button>
        </XStack>
        <Button
          variant="ghost"
          justifyContent="flex-start"
          colorScheme="neutral"
          event="Storefront Sort By Filters Clicked"
          onPress={() => setSortOpen(true)}
          data-testid="shopFiltersSortOrders"
        >
          <XStack space="$1" display="flex" alignItems="center">
            <Text numberOfLines={1} fontWeight="$semibold" size="$xs">
              {activeSortOrder.name}
            </Text>
            <CaretDownIcon size={16} color="$neutral.900" weight="bold" />
          </XStack>
        </Button>
      </XStack>
      <Sheet scroll open={filtersOpen} onOpenChange={setFiltersOpen}>
        <YStack space="$6">
          {!!activeFilter && (
            <H4
              size="$lg"
              {...shopAllLinkProps}
              onPress={() => {
                track('Storefront Shop All Button Clicked')
                setFiltersOpen(false)
                shopAllLinkProps.onPress()
              }}
            >
              Shop All
            </H4>
          )}
          {activeCategoryGroupings.map((categoryGrouping) => {
            return (
              <ShopFilterItem
                onPress={() => setFiltersOpen(false)}
                onChildItemPress={() => setFiltersOpen(false)}
                key={categoryGrouping.value}
                item={categoryGrouping}
              />
            )
          })}
          {/* There is no groupings for tags at the moment */}
          <ShopFilterItem
            tagMode
            item={activeTags[0]}
            onPress={() => setFiltersOpen(false)}
            onChildItemPress={() => setFiltersOpen(false)}
          />
        </YStack>
      </Sheet>
      <Sheet
        title="Sort by"
        open={sortOpen}
        onOpenChange={setSortOpen}
        snapPoints={[55]}
      >
        <SortOrders onItemPress={() => setSortOpen(false)} />
      </Sheet>
    </>
  )
}
